import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";

// Typeahead dropdown for high-cardinality lists (employees, assets, offices
// across orgs). Same prop shape as <Sl> so callers can swap with a one-line
// change. Keyboard nav: ↑/↓ to move, Enter to pick, Escape to close.
//
// options: [{ v, l, sub? }] — `sub` is an optional subtitle string shown
// dimmer beneath the label (e.g. employee_id, designation).
//
// Picking from a list of 800 entries through a native <select> is
// effectively impossible — this component is the pattern to use anywhere
// volume can grow.
export const SearchSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No matches",
  disabled = false,
  clearable = false,
  error = false,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0); // highlighted index
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const current = options.find(o => String(o.v) === String(value)) || null;

  const filtered = !q
    ? options
    : options.filter(o => {
        const s = q.toLowerCase();
        return (
          (o.l || "").toLowerCase().includes(s) ||
          (o.sub || "").toLowerCase().includes(s) ||
          String(o.v).toLowerCase().includes(s)
        );
      });

  // Cap rendered rows for runtime safety. Search narrows down quickly so
  // 200 visible at once is plenty without proper virtualization.
  const visible = filtered.slice(0, 200);
  const truncated = filtered.length > visible.length;

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    // Capture phase — a modal's own container commonly calls
    // e.stopPropagation() on mousedown (to stop a backdrop-click from
    // closing the whole modal), which would otherwise stop this bubbling
    // document listener from ever seeing clicks on OTHER fields in that
    // same modal, leaving this dropdown stuck open until the backdrop
    // itself is clicked. Capture fires top-down before any of that.
    document.addEventListener("mousedown", onClick, true);
    document.addEventListener("keydown", onEsc);
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onClick, true);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => { setHi(0); }, [q, open]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi(h => Math.min(h + 1, visible.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && visible[hi]) {
      e.preventDefault();
      onChange(visible[hi].v);
      setOpen(false);
      setQ("");
    }
  };

  return (
    <div ref={wrapRef} className="relative w-full min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={`w-full flex items-center gap-2 px-[10px] py-[7px] bg-bg2 border ${error ? "border-r" : "border-b0"} rounded-[10px] text-[14px] text-t0 outline-none cursor-pointer disabled:opacity-60`}
      >
        <span className={`flex-1 truncate text-left ${current ? "text-t0" : "text-t3"}`}>
          {current ? current.l : placeholder}
        </span>
        {clearable && current && (
          <span
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="text-t3 hover:text-t0"
          ><X size={12} /></span>
        )}
        <ChevronDown size={14} className="text-t3 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-[60] mt-1 left-0 right-0 bg-bg1 border border-b1 rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center gap-1 p-2 border-b border-b0">
            <Search size={12} className="text-t3 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              className="flex-1 min-w-0 bg-transparent outline-none text-[14px] text-t0"
            />
            {q && <button onClick={() => setQ("")} className="text-t3 hover:text-t0 bg-transparent border-0 cursor-pointer"><X size={12} /></button>}
          </div>

          <div className="max-h-[260px] overflow-auto">
            {visible.length === 0 ? (
              <div className="text-center text-[14px] text-t3 py-4">{emptyText}</div>
            ) : (
              visible.map((o, i) => (
                <div
                  key={String(o.v)}
                  onClick={() => { onChange(o.v); setOpen(false); setQ(""); }}
                  onMouseEnter={() => setHi(i)}
                  className={`flex items-center gap-2 px-3 py-[7px] cursor-pointer ${i === hi ? "bg-pg" : "hover:bg-sh"}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-[14px] truncate ${i === hi ? "text-p font-semibold" : "text-t0"}`}>{o.l}</div>
                    {o.sub && <div className="text-[12px] text-t3 truncate">{o.sub}</div>}
                  </div>
                  {String(o.v) === String(value) && <span className="text-[14px] text-p font-bold flex-shrink-0">✓</span>}
                </div>
              ))
            )}
            {truncated && (
              <div className="text-[12px] text-t3 text-center py-2 border-t border-b0">
                Showing first {visible.length} of {filtered.length} — type to narrow
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
